import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { Paywall } from './components/Paywall/Paywall';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { SupportProvider } from './contexts/SupportContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AnalyticsPage } from './pages/AnalyticsPage/AnalyticsPage';
import { AuthPage } from './pages/AuthPage/AuthPage';
import { HelpPage } from './pages/HelpPage/HelpPage';
import { OnboardingPage } from './pages/OnboardingPage/OnboardingPage';
import { PricingPage } from './pages/PricingPage/PricingPage';
import { ProfilePage } from './pages/ProfilePage/ProfilePage';
import { SolutionsPage } from './pages/SolutionsPage/SolutionsPage';
import { QueryClientProvider } from './shared/queryClient';
import { LS_ORG_ID } from './shared/config/storageKeys';
import { isPaywallDisabled } from './shared/config/paywall';
import {
  isOrganizationOnboardingComplete,
  isSupportCustomerSession,
  pickOrgForOnboardingCheck,
  skipsOrgOnboardingWhenIncomplete,
} from './shared/onboarding/onboardingState';
import { migrateBrandStorageKeys } from './shared/utils/migrateBrandStorageKeys';
import { RequirePermission } from './components/auth';

const CalendarPage = lazy(() => import('./pages/CalendarPage/CalendarPage'));
const ForecastPage = lazy(() => import('./pages/ForecastPage/ForecastPage'));
const ReleasesPage = lazy(() => import('./pages/ReleasesPage/ReleasesPage'));
const CommitmentPage = lazy(() => import('./pages/CommitmentPage/CommitmentPage'));
const DeliveryReviewPage = lazy(() => import('./pages/DeliveryReviewPage/DeliveryReviewPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage/ReportsPage'));

const routeFallback = (
  <div
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}
  >
    <div className="loading-spinner" />
  </div>
);

const fullScreenSpinner = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div className="loading-spinner" />
  </div>
);

/**
 * Logged-out Relix home is the commercial offer (RLX-FEAT-011).
 * URL stays `/` so PostHog `$pageview` pathname `/` vs `/auth` is distinct —
 * do not add `marketing_home_view`. Logged-in users go to the product, not marketing.
 */
function HomeIndex() {
  const { user, loading, initialized } = useAuth();

  if (!initialized || loading) {
    return fullScreenSpinner;
  }

  if (user) {
    return <Navigate to="/analytics" replace />;
  }

  return <SolutionsPage />;
}

/** Renders child routes only when authenticated */
function RequireAuth() {
  const { user, loading, initialized } = useAuth();

  if (!initialized || loading) {
    return fullScreenSpinner;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
}

/** Requires at least one organization and completed first-run onboarding */
function RequireOrg() {
  const { organizations, initialized, loading } = useAuth();
  if (!initialized || loading) {
    return fullScreenSpinner;
  }
  if (isSupportCustomerSession()) {
    return <Outlet />;
  }
  if (organizations.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }
  const org = pickOrgForOnboardingCheck(
    organizations,
    typeof window !== 'undefined' ? localStorage.getItem(LS_ORG_ID) : null
  );
  if (org && !isOrganizationOnboardingComplete(org) && !skipsOrgOnboardingWhenIncomplete(org)) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

/** Blocks app when subscription / trial expired (owners only) */
function RequireSubscription() {
  const { currentOrg, loading: authLoading } = useAuth();
  const { loading: subLoading, isActive } = useSubscription();
  const [paywallDismissed, setPaywallDismissed] = React.useState(false);

  if (authLoading || subLoading || !currentOrg) {
    return fullScreenSpinner;
  }

  if (isPaywallDisabled()) {
    return <Outlet />;
  }

  const orgIncomplete = !isOrganizationOnboardingComplete(currentOrg);

  // Never show paywall for non-owners or for orgs that haven't completed onboarding yet.
  // Members inherit the owner's subscription; incomplete orgs haven't started billing.
  if (!isActive && currentOrg.role === 'owner' && !orgIncomplete && !paywallDismissed) {
    return <Paywall onDismiss={() => setPaywallDismissed(true)} />;
  }
  return <Outlet />;
}

function AppRoutes() {
  const { initialized } = useAuth();

  if (!initialized) {
    return fullScreenSpinner;
  }

  return (
    <Suspense fallback={routeFallback}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomeIndex />} />
          <Route path="auth" element={<AuthPage />} />
          <Route path="solutions" element={<SolutionsPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route element={<RequireAuth />}>
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route element={<RequireOrg />}>
              <Route element={<RequireSubscription />}>
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="releases" element={<ReleasesPage />} />
                <Route
                  path="reports"
                  element={
                    <RequirePermission perm="reports.access" sectionName="Reports">
                      <ReportsPage />
                    </RequirePermission>
                  }
                />
                <Route path="forecast" element={<ForecastPage />} />
                <Route path="settings" element={<ProfilePage />} />
                <Route path="profile" element={<Navigate to="/settings" replace />} />
                <Route
                  path="integrations"
                  element={<Navigate to="/settings?tab=project" replace />}
                />
                <Route path="commitment" element={<CommitmentPage />} />
                <Route path="delivery-review" element={<DeliveryReviewPage />} />
                <Route path="reports-builder" element={<Navigate to="/reports" replace />} />
                <Route path="*" element={<Navigate to="/analytics" replace />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

/** Wires SupportProvider inside AuthProvider so it gets isSupportUser */
function SupportAwareApp() {
  const { isSupportUser, authMeSucceeded } = useAuth();
  return (
    <SupportProvider isSupportUser={isSupportUser} authMeSucceeded={authMeSucceeded}>
      <SubscriptionProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </SubscriptionProvider>
    </SupportProvider>
  );
}

// Run one-time localStorage key migration (workalytix_* / svodion_* → relix_*)
migrateBrandStorageKeys();

function App() {
  return (
    <QueryClientProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <SupportAwareApp />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
